(() => {
  if (customElements.get('boxzy-gift-options')) return;

  class BoxzyGiftOptions extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        document.addEventListener('boxzy:gift-options-saved', this.syncHandler);
        return;
      }
      this.initialized = true;
      this.dialog = this.querySelector('dialog');
      this.trigger = this.querySelector('[data-gift-open]');
      this.messageEnabled = this.querySelector('[data-gift-message-enabled]');
      this.message = this.querySelector('[data-gift-message]');
      this.messagePanel = this.querySelector('[data-gift-message-panel]');
      this.receipt = this.querySelector('[data-gift-receipt]');
      this.wrapping = this.querySelector('[data-gift-wrapping]');
      this.wrapVariant = this.dataset.wrapVariant || '';
      this.saveButton = this.querySelector('[data-gift-save]');
      this.removeButton = this.querySelector('[data-gift-remove]');
      this.closeButton = this.querySelector('[data-gift-close]');
      this.error = this.querySelector('[data-gift-error]');
      this.saveLabel = this.saveButton.textContent;
      this.state = { message: this.dataset.message || '', receipt: this.dataset.receipt === 'Yes', wrapping: Number(this.dataset.wrapQuantity) > 0 };
      this.receiptAllowed = this.dataset.receiptAllowed === 'true';
      this.trigger.addEventListener('click', () => this.open());
      this.closeButton.addEventListener('click', () => { if (!this.busy) this.dialog.close(); });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog && !this.busy) {
          const bounds = this.dialog.getBoundingClientRect();
          if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) this.dialog.close();
        }
      });
      this.dialog.addEventListener('cancel', (event) => { if (this.busy) event.preventDefault(); });
      this.dialog.addEventListener('close', () => {
        if (!document.querySelector('boxzy-gift-options dialog[open]')) document.documentElement.classList.remove('boxzy-gift-modal-open');
        this.trigger.focus();
      });
      this.messageEnabled.addEventListener('change', () => {
        this.updateMessageField();
        if (this.messageEnabled.checked) this.message.focus();
      });
      this.message.addEventListener('input', () => {
        this.message.setCustomValidity('');
        this.updateCounter();
      });
      this.saveButton.addEventListener('click', () => this.save(false));
      this.removeButton.addEventListener('click', () => this.save(true));
      this.syncHandler = (event) => {
        this.state = this.readState(event.detail);
        this.renderSummary();
      };
      document.addEventListener('boxzy:gift-options-saved', this.syncHandler);
      this.renderSummary();
    }

    disconnectedCallback() {
      document.removeEventListener('boxzy:gift-options-saved', this.syncHandler);
      if (this.dialog?.open) this.dialog.close();
    }

    readState(cart = {}) {
      const attributes = cart.attributes || {};
      return { message: String(attributes['Gift Message'] || ''), receipt: attributes['Gift Receipt'] === 'Yes', wrapping: this.wrapLines(cart).some(item => item.quantity > 0) };
    }

    wrapLines(cart) {
      return (cart.items || []).filter(item => String(item.variant_id) === this.wrapVariant);
    }

    async getCart() {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(root + 'cart.js', { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('Unable to read cart');
      return response.json();
    }

    async open() {
      this.error.hidden = true;
      this.setFields();
      this.dialog.showModal();
      document.documentElement.classList.add('boxzy-gift-modal-open');
      this.setBusy(true);
      try {
        const cart = await this.getCart();
        this.state = this.readState(cart);
        this.setFields();
        this.renderSummary();
      } catch {
        this.showError();
      } finally {
        this.setBusy(false);
      }
    }

    setFields() {
      this.message.value = this.state.message;
      this.messageEnabled.checked = Boolean(this.state.message);
      if (this.receipt) this.receipt.checked = this.state.receipt;
      if (this.wrapping) this.wrapping.checked = this.state.wrapping;
      this.updateMessageField();
    }

    updateMessageField() {
      const enabled = this.messageEnabled.checked;
      this.message.disabled = !enabled;
      this.message.required = enabled;
      this.messagePanel.hidden = !enabled;
      this.updateCounter();
    }

    updateCounter() {
      this.querySelector('[data-gift-count]').textContent = String(this.message.value.length);
    }

    renderSummary() {
      const active = Boolean(this.state.message || this.state.receipt || this.state.wrapping);
      this.querySelector('[data-gift-trigger-label]').textContent = active ? this.dataset.editLabel : this.dataset.addLabel;
      const freeLabel = this.state.message && this.state.receipt ? this.dataset.bothLabel : this.state.message ? this.dataset.messageLabel : this.state.receipt ? this.dataset.receiptLabel : '';
      const label = [freeLabel, this.state.wrapping ? this.dataset.wrapSavedLabel : ''].filter(Boolean).join(' ') || this.dataset.emptyLabel;
      this.querySelector('[data-gift-summary]').textContent = label;
      this.trigger.setAttribute('aria-label', (active ? this.dataset.editLabel : this.dataset.addLabel) + '. ' + label);
    }

    setBusy(busy) {
      this.busy = busy;
      this.dialog.setAttribute('aria-busy', String(busy));
      this.saveButton.disabled = busy;
      this.removeButton.disabled = busy;
      this.closeButton.disabled = busy;
      this.messageEnabled.disabled = busy;
      this.message.disabled = busy || !this.messageEnabled.checked;
      if (this.receipt) this.receipt.disabled = busy;
      if (this.wrapping) this.wrapping.disabled = busy;
      this.saveButton.textContent = busy ? this.dataset.savingLabel : this.saveLabel;
    }

    showError() {
      this.error.textContent = this.dataset.errorLabel;
      this.error.hidden = false;
    }

    async save(remove) {
      if (this.busy) return;
      if (!remove && this.messageEnabled.checked) {
        this.message.setCustomValidity(this.message.value.trim() ? '' : this.dataset.requiredLabel);
        if (!this.message.reportValidity()) return;
      }
      this.error.hidden = true;
      this.setBusy(true);
      try {
        const cart = await this.getCart();
        const message = !remove && this.messageEnabled.checked ? this.message.value.trim() : '';
        const receipt = !remove && this.receiptAllowed && Boolean(this.receipt?.checked);
        const wrapping = !remove && Boolean(this.wrapping?.checked);
        const wrapLines = this.wrapLines(cart);
        const updates = {};
        wrapLines.forEach((item, index) => { updates[item.key] = wrapping && index === 0 ? 1 : 0; });
        if (wrapping && !wrapLines.length) updates[this.wrapVariant] = 1;
        const quantityChanged = wrapLines.reduce((total, item) => total + item.quantity, 0) !== (wrapping ? 1 : 0) || wrapLines.length > 1;
        const attributes = { ...cart.attributes, 'Gift Message': message, 'Gift Receipt': receipt ? 'Yes' : '', 'Gift Options': message || receipt || wrapping ? 'Yes' : '' };
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes, updates })
        });
        if (!response.ok) throw new Error('Unable to save gift options');
        const updated = await response.json();
        const saved = this.readState(updated);
        const savedWrapQuantity = this.wrapLines(updated).reduce((total, item) => total + item.quantity, 0);
        if (saved.message !== message || saved.receipt !== receipt || savedWrapQuantity !== (wrapping ? 1 : 0)) throw new Error('Gift options were not saved');
        document.dispatchEvent(new CustomEvent('boxzy:gift-options-saved', { detail: updated }));
        this.dialog.close();
        if (quantityChanged) window.location.reload();
      } catch {
        this.showError();
      } finally {
        this.setBusy(false);
      }
    }
  }

  customElements.define('boxzy-gift-options', BoxzyGiftOptions);
})();

